import { addDays, weekDays, weekStartOf } from "@astrum/shared";
import type { TimeEntry, TimesheetStatus } from "@astrum/shared";
import type { PillTone } from "@astrum/ui";

/**
 * Small pure helpers behind the timer and the timesheet screen. The week and
 * minute maths proper lives in `@astrum/shared` so the server cuts weeks the
 * same way; what is here is only about showing it.
 */

/** "1:05:09" — a running timer's face. Hours are not padded; minutes and seconds are. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Minutes a running timer has accrued so far, for totals that include it. */
export function liveMinutes(entry: Pick<TimeEntry, "running" | "startedAt" | "minutes">, now: number): number {
  if (!entry.running || !entry.startedAt) return entry.minutes;
  return Math.max(0, Math.floor((now - new Date(entry.startedAt).getTime()) / 60_000));
}

/** The Monday `weeks` weeks from `weekStart` (negative goes back). */
export function shiftWeek(weekStart: string, weeks: number): string {
  return addDays(weekStartOf(weekStart), weeks * 7);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dayPart = (key: string) => `${Number(key.slice(8, 10))} ${MONTHS[Number(key.slice(5, 7)) - 1]}`;

/** "5 Oct – 11 Oct 2026" */
export function weekLabel(weekStart: string): string {
  const days = weekDays(weekStart);
  return `${dayPart(days[0]!)} – ${dayPart(days[6]!)} ${days[6]!.slice(0, 4)}`;
}

/** Column heading for a day of the week: "Mon 5". */
export function dayHeading(weekStart: string, index: number): string {
  return `${WEEKDAYS[index]} ${Number(weekDays(weekStart)[index]!.slice(8, 10))}`;
}

export const STATUS_LABEL: Record<TimesheetStatus, string> = {
  draft: "Open",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Sent back",
};

export const STATUS_TONE: Record<TimesheetStatus, PillTone> = {
  draft: "neutral",
  submitted: "info",
  approved: "ok",
  rejected: "danger",
};

/** The owner may change a week unless it is waiting on, or has passed, approval. */
export const isEditableStatus = (status: TimesheetStatus): boolean => status === "draft" || status === "rejected";
